import {
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { ModalComponent, InlineInfoComponent, SpinnerComponent } from '@catch/general-ui';
import { MarkdownViewerComponent } from '@catch/markdown-viewer';
import { TranslocoPipe } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AsyncPipe } from '@angular/common';
import { HeaderTagsComponent } from '../../snippets/header-tags/header-tags.component';
import { AnimatedCheckComponent } from '../../snippets/animated-check/animated-check.component';
import { AnimatedCrossComponent } from '../../snippets/animated-cross/animated-cross.component';
import { Observable, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JobboardAdapter } from '../../../adapters/jobboard.adapter';
import { CompanyInfo } from '../../../interfaces/company-info';
import { JobDetail } from '../../../interfaces/job-detail';
import { ColorService } from '../../../services/color.service';
import { EmbedService } from '../../../services/embed.service';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { GoogleJobPostingService } from '../../../services/google-job-posting.service';
import { RefService } from '../../../services/ref.service';
import { SEOService } from '../../../services/seo.service';

@UntilDestroy()
@Component({
  selector: 'app-job-recommended',
  templateUrl: './job-recommended.component.html',
  styleUrls: ['./job-recommended.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    TranslocoPipe,
    ModalComponent,
    InlineInfoComponent,
    MarkdownViewerComponent,
    SpinnerComponent,
    HeaderTagsComponent,
    AnimatedCheckComponent,
    AnimatedCrossComponent,
    FaIconComponent
],
})
export class JobRecommendedComponent implements OnInit {
  private readonly colorService = inject(ColorService);
  private readonly googleJobsService = inject(GoogleJobPostingService);
  private readonly jobboardAdapter = inject(JobboardAdapter);
  private readonly refService = inject(RefService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seoService = inject(SEOService);
  public readonly embedService = inject(EmbedService);
  public readonly fontawesomeService = inject(FontawesomeService);

  @ViewChild('prefillModal') prefillModal: ModalComponent;
  job$: Observable<JobDetail>;
  companyInfo$: Observable<CompanyInfo>;
  linkValid: WritableSignal<boolean> = signal(false);
  jobId: number;
  nonce: string;

  loading: WritableSignal<boolean> = signal(false);
  submitSuccess: WritableSignal<boolean> = signal(false);

  ngOnInit(): void {
    this.job$ = this.route.data.pipe(map((data: Data) => data['job']));
    this.companyInfo$ = this.route.data.pipe(
      map((data: Data) => data['companyInfo'])
    );
    this.companyInfo$.pipe(untilDestroyed(this)).subscribe({
      next: (company: CompanyInfo) => {
        this.seoService.updateImage(company.logoURL);
      },
    });
    zip(this.job$, this.companyInfo$)
      .pipe(untilDestroyed(this))
      .subscribe(([job, companyInfo]) => {
        this.googleJobsService.setStructuredData(job, companyInfo);
      });
    const nonce = this.route.snapshot.queryParams['nonce'];
    this.job$
      .pipe(
        tap((job: JobDetail) => {
          this.jobId = job.id;
          this.colorService.setDesign(job.companyID);
          this.seoService.setWebsite(job.website);
          this.refService.countJobClick(job.companyID, job.id);
          if (nonce) {
            this.nonce = nonce;
            this.jobboardAdapter
              .getPrefillData(nonce, job.id)
              .subscribe((data) => this.linkValid.set(data.valid));
          } else {
            this.linkValid.set(false);
          }
        }),
        switchMap((job: JobDetail) => this.seoService.updateJobPageSeo(job)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  applyWithPrefillData() {
    this.loading.set(true);
    this.jobboardAdapter
      .applyWithPrefillData(this.jobId, this.nonce)
      .subscribe({
        next: () => {
          this.submitSuccess.set(true);
          this.loading.set(false);
          this.prefillModal.close();
        },
        error: () => this.loading.set(false),
      });
  }

  goToManualApply() {
    this.job$.subscribe((job) => {
      this.router.navigate(
        ['/companies', job.companyID, 'jobs', job.id, 'apply'],
        {
          queryParams: {
            recommendationNonce: this.nonce,
          },
        }
      );
    });
  }
}
